import type { EventType } from '../types/planner';
import { EVENT_TYPE_COLORS } from '../types/planner';

const VALID_TYPES = new Set<EventType>([
  'appointment',
  'birthday',
  'school',
  'sport',
  'social',
  'work',
  'holiday',
  'meal',
  'chore',
  'reminder',
  'arts',
  'other',
]);

// Map hex colors (normalized to lowercase) to EventType
const HEX_COLOR_TO_TYPE: Record<string, EventType> = Object.entries(EVENT_TYPE_COLORS).reduce(
  (acc, [type, hex]) => ({ ...acc, [hex.toLowerCase()]: type as EventType }),
  {} as Record<string, EventType>
);

/**
 * Infer or preserve the EventType for an event based on explicit external categories,
 * event title keywords, hex color matching, or existing type.
 */
export function inferEventType(
  title: string,
  color?: string,
  externalCategory?: string,
  existingType?: EventType
): EventType {
  // 1. If existingType is explicit and not 'other', preserve it
  if (existingType && existingType !== 'other' && VALID_TYPES.has(existingType)) {
    return existingType;
  }

  // 2. Check external category parameter (e.g. from Google extendedProperties or iCal CATEGORIES)
  if (externalCategory) {
    const normCat = externalCategory.trim().toLowerCase();
    if (VALID_TYPES.has(normCat as EventType)) {
      return normCat as EventType;
    }
    // Check if externalCategory text contains keywords
    const catMatch = matchTitleKeywords(normCat);
    if (catMatch) return catMatch;
  }

  // 3. Match title keywords
  if (title && title.trim()) {
    const titleMatch = matchTitleKeywords(title);
    if (titleMatch) return titleMatch;
  }

  // 4. Match color to known Fambee event colors
  if (color) {
    const normColor = color.toLowerCase();
    if (HEX_COLOR_TO_TYPE[normColor]) {
      return HEX_COLOR_TO_TYPE[normColor];
    }
  }

  return 'other';
}

function matchTitleKeywords(text: string): EventType | null {
  const lower = text.toLowerCase();

  if (
    lower.includes('doctor') ||
    lower.includes('dentist') ||
    lower.includes('appointment') ||
    lower.includes('checkup') ||
    lower.includes('physio') ||
    lower.includes('clinic') ||
    lower.includes('vet') ||
    lower.includes('hospital') ||
    lower.includes('medical') ||
    lower.includes('dr.') ||
    lower.includes('dr ') ||
    lower.includes('specialist')
  ) {
    return 'appointment';
  }

  if (
    lower.includes('birthday') ||
    lower.includes('bday') ||
    lower.includes('cake') ||
    lower.includes('anniversary')
  ) {
    return 'birthday';
  }

  if (
    lower.includes('school') ||
    lower.includes('exam') ||
    lower.includes('homework') ||
    lower.includes('class') ||
    lower.includes('tutor') ||
    lower.includes('test') ||
    lower.includes('lecture') ||
    lower.includes('assembly') ||
    lower.includes('parent teacher') ||
    lower.includes('report card') ||
    lower.includes('excursion')
  ) {
    return 'school';
  }

  if (
    lower.includes('soccer') ||
    lower.includes('football') ||
    lower.includes('sport') ||
    lower.includes('match') ||
    lower.includes('gym') ||
    lower.includes('swim') ||
    lower.includes('practice') ||
    lower.includes('tennis') ||
    lower.includes('game') ||
    lower.includes('training') ||
    lower.includes('basketball') ||
    lower.includes('netball') ||
    lower.includes('run') ||
    lower.includes('marathon') ||
    lower.includes('fitness') ||
    lower.includes('workout') ||
    lower.includes('karate') ||
    lower.includes('taekwondo')
  ) {
    return 'sport';
  }

  if (
    lower.includes('trip') ||
    lower.includes('vacation') ||
    lower.includes('holiday') ||
    lower.includes('public holiday') ||
    lower.includes('bank holiday') ||
    lower.includes('day off') ||
    lower.includes('christmas') ||
    lower.includes('xmas') ||
    lower.includes('new year') ||
    lower.includes('easter') ||
    lower.includes('good friday') ||
    lower.includes('anzac') ||
    lower.includes('memorial day') ||
    lower.includes('labor day') ||
    lower.includes('labour day') ||
    lower.includes('independence day') ||
    lower.includes('king\'s birthday') ||
    lower.includes('queen\'s birthday') ||
    lower.includes('boxing day') ||
    lower.includes('thanksgiving') ||
    lower.includes('halloween') ||
    lower.includes('veterans day') ||
    lower.includes('columbus day') ||
    lower.includes('juneteenth') ||
    lower.includes('presidents day') ||
    lower.includes('martin luther king') ||
    lower.includes('flight') ||
    lower.includes('hotel') ||
    lower.includes('beach') ||
    lower.includes('resort') ||
    lower.includes('cruise') ||
    lower.includes('camping')
  ) {
    return 'holiday';
  }

  if (
    lower.includes('dinner') ||
    lower.includes('lunch') ||
    lower.includes('breakfast') ||
    lower.includes('brunch') ||
    lower.includes('restaurant') ||
    lower.includes('bbq') ||
    lower.includes('supper') ||
    lower.includes('meal') ||
    lower.includes('cookout')
  ) {
    return 'meal';
  }

  if (
    lower.includes('art') ||
    lower.includes('music') ||
    lower.includes('piano') ||
    lower.includes('guitar') ||
    lower.includes('drum') ||
    lower.includes('drums') ||
    lower.includes('drumming') ||
    lower.includes('lesson') ||
    lower.includes('violin') ||
    lower.includes('flute') ||
    lower.includes('singing') ||
    lower.includes('vocal') ||
    lower.includes('band') ||
    lower.includes('rehearsal') ||
    lower.includes('orchestra') ||
    lower.includes('dance') ||
    lower.includes('ballet') ||
    lower.includes('painting') ||
    lower.includes('drama') ||
    lower.includes('theater') ||
    lower.includes('theatre') ||
    lower.includes('concert') ||
    lower.includes('choir') ||
    lower.includes('pottery') ||
    lower.includes('craft')
  ) {
    return 'arts';
  }

  if (
    lower.includes('party') ||
    lower.includes('social') ||
    lower.includes('hangout') ||
    lower.includes('drinks') ||
    lower.includes('bar') ||
    lower.includes('playdate') ||
    lower.includes('reunion')
  ) {
    return 'social';
  }

  if (
    lower.includes('work') ||
    lower.includes('meeting') ||
    lower.includes('office') ||
    lower.includes('sync') ||
    lower.includes('client') ||
    lower.includes('interview') ||
    lower.includes('presentation') ||
    lower.includes('workshop') ||
    lower.includes('conference')
  ) {
    return 'work';
  }

  if (
    lower.includes('remind') ||
    lower.includes('pay') ||
    lower.includes('bill') ||
    lower.includes('renew') ||
    lower.includes('tax') ||
    lower.includes('registration')
  ) {
    return 'reminder';
  }

  if (
    lower.includes('clean') ||
    lower.includes('mow') ||
    lower.includes('trash') ||
    lower.includes('laundry') ||
    lower.includes('bin') ||
    lower.includes('chore') ||
    lower.includes('vacuum')
  ) {
    return 'chore';
  }

  return null;
}
